import { useState, useEffect, useRef } from "react";
import { MessageCircle, X, Send, Loader } from "lucide-react";

const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId] = useState(() => `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      // Welcome message
      setMessages([{
        role: "assistant",
        content: "Hallo! 👋 Ich bin der KI-Assistent von Freys Media. Wie kann ich dir bei deinem Videoprojekt helfen?"
      }]);
    }
  }, [isOpen]);

  const sendMessage = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage = input.trim();
    setInput("");
    // Verlauf VOR dem Anhängen der neuen Nachricht (für Kontext an den Bot)
    const history = messages
      .filter((m) => m.role === "user" || m.role === "assistant")
      .map((m) => ({ role: m.role, content: m.content }));
    setMessages(prev => [...prev, { role: "user", content: userMessage }]);
    setIsLoading(true);

    try {
      const response = await fetch(`/api/chatbot/chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          session_id: sessionId,
          message: userMessage,
          history
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Fehler beim Senden der Nachricht");
      }

      const data = await response.json();
      setMessages(prev => [...prev, { role: "assistant", content: data.response }]);
      
    } catch (error) {
      console.error("Chat error:", error);
      setMessages(prev => [...prev, { 
        role: "assistant", 
        content: error.message || "Entschuldigung, es gab einen Fehler. Bitte versuche es erneut oder kontaktiere Kenny direkt über WhatsApp: +49 173 4793306" 
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      {/* Chat Button with Label */}
      <div className="fixed bottom-6 right-6 z-[100] flex flex-col items-end gap-2">
        {!isOpen && (
          <div className="bg-gradient-to-r from-[hsl(var(--accent))] to-purple-600 text-white px-4 py-2 rounded-full text-sm font-medium shadow-lg animate-bounce">
            💬 Frag mich etwas!
          </div>
        )}
        
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="w-16 h-16 bg-gradient-to-br from-[hsl(var(--accent))] to-purple-600 text-white rounded-full shadow-2xl hover:scale-110 transition-all duration-300 flex items-center justify-center relative"
          style={{ boxShadow: '0 0 30px rgba(147, 51, 234, 0.7)' }}
          aria-label="Chat öffnen"
        >
          {/* Pulse ring animation */}
          <span className="absolute inset-0 rounded-full bg-[hsl(var(--accent))] animate-ping opacity-75"></span>
          
          {/* Icon */}
          <span className="relative">
            {isOpen ? <X className="w-7 h-7" /> : <MessageCircle className="w-7 h-7" />}
          </span>
          
          {/* Badge */}
          {!isOpen && (
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-xs font-bold">
              AI
            </span>
          )}
        </button>
      </div>

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-24 right-6 z-[100] w-96 h-[500px] bg-background border border-white/10 rounded-2xl shadow-2xl flex flex-col overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-[hsl(var(--accent))] to-purple-600 p-4 text-white">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <MessageCircle className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-semibold">Freys Media Assistent</h3>
                <p className="text-xs opacity-90">Powered by Claude AI</p>
              </div>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-secondary/20">
            {messages.map((msg, index) => (
              <div
                key={index}
                className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[80%] p-3 rounded-2xl ${
                    msg.role === "user"
                      ? "bg-gradient-to-br from-[hsl(var(--accent))] to-purple-600 text-white"
                      : "bg-secondary text-foreground"
                  }`}
                >
                  <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-secondary p-3 rounded-2xl">
                  <Loader className="w-5 h-5 animate-spin text-[hsl(var(--accent))]" />
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-4 border-t border-white/10 bg-background">
            <div className="flex gap-2">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Deine Nachricht..."
                className="flex-1 px-4 py-2 bg-secondary border border-white/10 rounded-xl focus:outline-none focus:ring-2 focus:ring-[hsl(var(--accent))] text-sm"
                maxLength={500}
                disabled={isLoading}
              />
              <button
                onClick={sendMessage}
                disabled={!input.trim() || isLoading}
                className="px-4 py-2 bg-gradient-to-br from-[hsl(var(--accent))] to-purple-600 text-white rounded-xl hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Send className="w-5 h-5" />
              </button>
            </div>
            <p className="text-xs text-muted-foreground mt-2 text-center">
              Max. 500 Zeichen · 10 Nachrichten pro Stunde
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default Chatbot;
